/*
 * Project: Dynamedics Portal Web
 * Created Date: Friday June 25th 2021
 * Author: Nalinda Wijayagunawardhane
 * -----
 * Last Modified: Monday January 8th 2024 1:58:02 pm
 * Modified By: Nalinda Wijayagunawardhane at <nwijayagunawardhane@mitrai.com>
 * Modified By: Dinusha Madhuranga at <dmadhuranga@mitrai.com>
 *              KasunSKarunasekara at <kkarunasekara@mitrai.com>
 * -----
 * Copyright (c) 2021 Mitra Sparks
 * -----
 * HISTORY:
 * 2022-07-23	NRB	Added support for shorten rounded off numeric formatting.
 * 2022-05-19	NRB	Added get access token properties function.
 * 2022-05-06	added heatmap color pallet
 * 2021-07-02	NRB	Added the constants for Overall
 * 2022-07-05	KK Added percentage option to pie chart
 * 2024-01-08 KK Granularity manipulation
 */

import { first, filter, orderBy, sum } from 'lodash';
import { capitalCase } from 'change-case';
import moment from 'moment';
import {
  OVERALL,
  TEXT_DIMENSIONS,
  DATA_SET_DOC_TYPE,
  ACCESS_TOKEN,
  EMPTY_STRING,
  MONTH_NAMES
} from './constants';
import {
  fCurrency,
  fNumber,
  fPercent,
  fShortenNumber,
  fShortenNumberRounded
} from './formatNumber';
import jwtDecode from 'jwt-decode';
import { format, parseISO } from 'date-fns';
import { useSelector } from 'react-redux';

export const getOverall = (department) => department.name === OVERALL;
export const getAbsences = (element) => element.name === 'Absences';
export const getProperDepartments = (department) => department.name !== OVERALL;
export const getNoResponses = (element) => element.reason === 'No Response';
export const getLeaves = (element) => element.reason === 'Leave';
export const getSegmentsExceptAbsences = (element) =>
  element.name !== 'Absences';
export const getOne = (data) => {
  return first(data) || {};
};
export function descendingComparator(a, b, orderBy) {
  let aOrderBy =
    typeof a[orderBy] === 'string'
      ? a[orderBy].toLocaleLowerCase()
      : a[orderBy];
  let bOrderBy =
    typeof b[orderBy] === 'string'
      ? b[orderBy].toLocaleLowerCase()
      : b[orderBy];

  if (bOrderBy < aOrderBy) {
    return -1;
  }
  if (bOrderBy > aOrderBy) {
    return 1;
  }
  return 0;
}
export function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
export function applySortFilter(array, comparator, query, attribute) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  if (query) {
    array = filter(array, (_question) => {
      return (
        _question[attribute].toLowerCase().indexOf(query.toLowerCase()) !== -1
      );
    });
    return array;
  }
  return stabilizedThis.map((el) => el[0]);
}

export const hasDecimal = (num) => {
  return !!(num % 1);
};

export const containsRole = (roles = [], rolePrefix = '') => {
  return roles.reduce((a, r) => a || r.includes(rolePrefix), false);
};

export const getWorkplaceWellbeingDescriptors = (
  workplaceWellbeing,
  employeeEngagement
) => {
  const overall = getOne(workplaceWellbeing.departments.filter(getOverall));
  const previous = workplaceWellbeing.overall.previous;
  const current = workplaceWellbeing.overall.current;
  let descriptors = [];
  descriptors.push({
    label: 'Health Index',
    labelType: 'TEXT',
    value: current,
    description: `Your employees show ${current}% positivity towards your organisation.`
  });
  if (previous) {
    descriptors.push({
      label:
        current > previous
          ? 'Better'
          : current < previous
          ? 'Down'
          : 'No Change',
      labelType: 'ICON',
      value: current - previous,
      description: `${Math.abs(current - previous)}% ${
        current > previous ? 'up' : current < previous ? 'down' : 'change'
      } from last period.`
    });
  } else {
    descriptors.push({
      label: 'No Change',
      labelType: 'ICON',
      value: 0,
      description: 'We are yet to collect responses.'
    });
  }
  if (overall) {
    descriptors.push({
      label: 'Segments',
      labelType: 'ICON',
      value: overall.data,
      description:
        'These are the segments in which your organisational health is measured.'
    });
  }
  if (employeeEngagement) {
    const highlyEngaged = (
      getOne(employeeEngagement.departments.filter(getOverall))?.data || []
    ).find((segment) => segment.name === 'Highly Engaged') || {
      name: '',
      data: 0
    };
    if (highlyEngaged.data && highlyEngaged.data > 0)
      descriptors.push({
        label: 'Engagement',
        labelType: 'ICON',
        value: highlyEngaged.data || 0,
        description: `${
          highlyEngaged.data || 0
        }% of your employees are highly engaged.`
      });
  }
  return descriptors;
};

export function applySort(posts, sortBy) {
  if (sortBy === 'latest') {
    posts = orderBy(posts, ['createdAt'], ['desc']);
  }
  if (sortBy === 'oldest') {
    posts = orderBy(posts, ['createdAt'], ['asc']);
  }
  if (sortBy === 'popular') {
    posts = orderBy(posts, ['view'], ['desc']);
  }
  return posts;
}

export function chartFormatter(val, type, skipFormat) {
  switch (type) {
    case 'CURRENCY':
      return fCurrency(val);

    case 'PERCENT':
      let temp = val;
      if (temp < 1 && !skipFormat) {
        temp = temp * 100.0;
      }
      return fPercent(temp);

    case 'NUMBER':
      return fShortenNumber(val);

    case 'NUMBER-SHORT':
      return fShortenNumberRounded(val);

    default:
      return fNumber(val);
  }
}

export function getAsPercentage(val, total) {
  return (val / total) * 100;
}

export function getTotal(items, prop) {
  return items.reduce((a, b) => {
    return a + parseFloat(b[prop]);
  }, 0);
}

export function getAccessToken() {
  const accessToken = localStorage.getItem(ACCESS_TOKEN);
  if (accessToken) {
    return accessToken;
  }
  return EMPTY_STRING;
}

export function getChartColors(chartType) {
  if (chartType === 'pie') {
    return [
      '#8267BE', // dark purple
      '#3FA796', // green
      '#FFBD35', // yellow
      '#D885A3', // shade pink
      '#516BEB', // blue
      '#A267AC', // purple
      '#98DDCA' // light green
    ];
  } else if (['line', 'area'].includes(chartType)) {
    return [
      '#516BEB', // blue
      '#98DDCA', // light green,
      '#D885A3', // shade pink,
      '#3FA796', // green
      '#A267AC', // purple
      '#FFBD35', // yellow
      '#8267BE' // dark purple
    ];
  } else if (chartType === 'bar') {
    return [
      '#73BFE9', // light blue
      '#3FA796', // green
      '#FFBD35', // yellow
      '#8267BE', // dark purple
      '#D885A3', // shade pink
      '#516BEB', // blue
      '#A267AC', // purple
      '#98DDCA' // light green
    ];
  } else if (chartType === 'radar') {
    return [
      '#8267BE', // dark purple
      '#3FA796', // green
      '#FFBD35', // yellow
      '#D885A3', // shade pink
      '#516BEB', // blue
      '#A267AC', // purple
      '#98DDCA' // light green
    ];
  } else if (chartType === 'heatmap') {
    return [
      '#8267BE', // dark purple
      '#3FA796', // green
      '#FFBD35', // yellow
      '#D885A3', // shade pink
      '#516BEB', // blue
      '#A267AC', // purple
      '#98DDCA' // light green
    ];
  }
}

export function getRAGColors(value) {
  if (value >= 75) {
    return ['#3FA796']; // theme.palette.primary.main
  } else if (value > 65) {
    return ['#1890FF']; // theme.palette.info.main
  } else if (value > 51) {
    return ['#FFBD35']; // yellow
  } else if (value > 35) {
    return ['#FFC107']; // theme.palette.warning.main,
  } else {
    return ['#FF4842']; // theme.palette.error.main
  }
}

export function getMaterialColors(value) {
  if (value >= 80) {
    return 'primary';
  } else if (value > 65) {
    return 'secondary';
  } else if (value > 50) {
    return 'success';
  } else if (value > 35) {
    return 'info';
  } else if (value > 20) {
    return 'warning';
  } else {
    return 'error';
  }
}

export function formatMeasureLabel(label) {
  if (label.includes('.')) {
    const name = label.split('.')[1];
    return capitalCase(name);
  } else {
    return label;
  }
}

export function updateQueryStringParameter(uri, key, value) {
  var re = new RegExp('([?&])' + key + '=.*?(&|$)', 'i');
  var separator = uri.indexOf('?') !== -1 ? '&' : '?';
  if (uri.match(re)) {
    return uri.replace(re, '$1' + key + '=' + value + '$2');
  } else {
    return uri + separator + key + '=' + value;
  }
}

export function getTimeRangeByRangeName(timeFrame = ' ', index = 0) {
  const timerange = timeFrame.toLowerCase().split(' ');
  const rangeIndex = timerange[0] == 'this' ? 0 + index : 1 + index;
  const timePeriod = timerange[1];
  const startDate = format(
    new Date(moment().subtract(rangeIndex, timePeriod).startOf(timePeriod)._d),
    'yyyy-MM-dd'
  );
  const endDate = format(
    new Date(moment().subtract(rangeIndex, timePeriod).endOf(timePeriod)._d),
    'yyyy-MM-dd'
  );
  return [startDate, endDate];
}

export function updateSearchParams(name, value, history) {
  var newUrl = updateQueryStringParameter(window.location.href, name, value);
  history.replace({
    pathname: history.pathname,
    search: newUrl.split('?')[1]
  });
}

export const documentTypeFormater = (type) => {
  if (type === DATA_SET_DOC_TYPE.ESG_KPIS.VALUE) {
    return DATA_SET_DOC_TYPE.ESG_KPIS.LABEL;
  } else {
    return type;
  }
};

export const granularityToDateFormat = (granularity) => {
  switch (granularity) {
    case 'day':
      return 'dd MMM yyyy';
    case 'week':
      return "'W'W MMM";
    case 'quarter':
      return 'yyyy QQQ';
    case 'year':
      return 'yyyy';
    default:
      return 'MMM yyyy';
  }
};

export const getAccessTokenDecoded = (accessToken) => {
  return jwtDecode(accessToken);
};

export const getNestedProperty = (object, propertyPath) => {
  const propertyValue = propertyPath
    .split('.')
    .reduce((accumulatedResult, currentProperty) => {
      return accumulatedResult && accumulatedResult[currentProperty];
    }, object);
  return propertyValue;
};

export const getFileTypeLabel = (fileType) => {
  return `(Only ${fileType} will be accepted)`;
};

export const getFileSizeLabel = (fileSize) => {
  return `(Only ${fileSize} of maximum file size will be accepted)`;
};

export const checkUndefinedOrNull = (data) => {
  if (data == undefined || !data) {
    return true;
  }
  return false;
};

/**
 * Formats dates according to selected granularity.
 * @param {*} date - Date from Cube data ISO format.
 * @param {*} granularity - Selected granularity i.e. year, month, quarter
 * @returns - Formatted date.
 */
export const formatDateLabel = (date, granularity) => {
  try {
    if (granularity == 'week') {
      return formatWeekOfMonth(date);
    } else {
      return format(parseISO(date), granularityToDateFormat(granularity));
    }
  } catch (error) {
    return date;
  }
};

const formatWeekOfMonth = (dateString) => {
  const date = parseISO(dateString);
  const weekNumber = Math.ceil((date.getDate() - date.getDay() + 1) / 7);
  const month = format(date, 'MMM');
  return `W${weekNumber} ${month}`;
};

export const getIRPeriod = (dateRangeName) => {
  const rangeType = dateRangeName.toLowerCase().split(' ')[0];
  const timerange = dateRangeName.toLowerCase().split(' ')[1];

  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  switch (timerange) {
    case 'month': {
      if (today.getMonth() == 0) {
        const monthName = MONTH_NAMES[11];
        const derivedYear = currentYear - 1;
        return `${monthName} ${derivedYear}`;
      } else {
        const month =
          rangeType == 'this' ? today.getMonth() : today.getMonth() - 1;
        const monthName = MONTH_NAMES[month];
        return `${monthName} ${currentYear}`;
      }
    }
    case 'quarter': {
      var quarter =
        rangeType == 'this'
          ? Math.floor((currentMonth + 3) / 3)
          : Math.floor((currentMonth + 3) / 3) - 1;
      if (quarter == 0) {
        quarter = 4;
        const derivedYear = currentYear - 1;
        return `Q${quarter} ${derivedYear}`;
      }
      return `Q${quarter} ${currentYear}`;
    }
    case 'year':
      const year =
        rangeType == 'this' ? today.getFullYear() : today.getFullYear() - 1;
      return year;
    default:
      return dateRangeName;
  }
};

export const getDashboardDateRange = (selectedOption, fiscalYearDetails) => {
  const today = new Date();
  let startDate, endDate, dateLabel;

  switch (selectedOption) {
    case '1m':
      startDate = new Date(
        today.getFullYear(),
        today.getMonth() - 1,
        today.getDate() + 1
      );
      endDate = today;

      //set UTC time
      startDate = new Date(
        Date.UTC(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate(),
          0,
          0,
          0
        )
      );
      endDate = new Date(
        Date.UTC(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate(),
          0,
          0,
          0
        )
      );

      dateLabel = `${getMonthAbbreviation(
        startDate.getMonth()
      )} ${startDate.getDate()} - ${getMonthAbbreviation(
        endDate.getMonth()
      )} ${endDate.getDate()}`;
      break;

    case '3m':
      startDate = new Date(
        today.getFullYear(),
        today.getMonth() - 3,
        today.getDate() + 1
      );
      endDate = today;

      //set UTC time
      startDate = new Date(
        Date.UTC(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate(),
          0,
          0,
          0
        )
      );
      endDate = new Date(
        Date.UTC(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate(),
          0,
          0,
          0
        )
      );

      dateLabel = `${getMonthAbbreviation(
        startDate.getMonth()
      )} ${startDate.getFullYear()} - ${getMonthAbbreviation(
        endDate.getMonth()
      )} ${endDate.getFullYear()}`;
      break;

    case '6m':
      startDate = new Date(
        today.getFullYear(),
        today.getMonth() - 6,
        today.getDate() + 1
      );
      endDate = today;

      //set UTC time
      startDate = new Date(
        Date.UTC(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate(),
          0,
          0,
          0
        )
      );
      endDate = new Date(
        Date.UTC(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate(),
          0,
          0,
          0
        )
      );

      dateLabel = `${getMonthAbbreviation(
        startDate.getMonth()
      )} ${startDate.getFullYear()} - ${getMonthAbbreviation(
        endDate.getMonth()
      )} ${endDate.getFullYear()}`;
      break;

    case 'ytd':
      if (
        fiscalYearDetails &&
        Object.keys(fiscalYearDetails)?.length > 0 &&
        !checkUndefinedOrNull(fiscalYearDetails?.fiscalYearStartDate)
      ) {
        startDate = new Date(fiscalYearDetails?.fiscalYearStartDate);
      } else {
        startDate = new Date(today.getFullYear(), 0, 1);
      }
      endDate = today;

      //set UTC time
      startDate = new Date(
        Date.UTC(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate(),
          0,
          0,
          0
        )
      );
      endDate = new Date(
        Date.UTC(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate(),
          0,
          0,
          0
        )
      );

      dateLabel = `Jan ${today.getFullYear()} - ${getMonthAbbreviation(
        endDate.getMonth()
      )} ${endDate.getDate()} ${endDate.getFullYear()}`;
      break;

    case '1y':
      startDate = new Date(
        today.getFullYear() - 1,
        today.getMonth(),
        today.getDate() + 1
      );
      endDate = today;

      //setup the UTC Time
      startDate = new Date(
        Date.UTC(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate(),
          0,
          0,
          0
        )
      );
      endDate = new Date(
        Date.UTC(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate(),
          0,
          0,
          0
        )
      );

      dateLabel = `${getMonthAbbreviation(
        startDate.getMonth()
      )} ${startDate.getFullYear()} - ${getMonthAbbreviation(
        endDate.getMonth()
      )} ${endDate.getFullYear()}`;
      break;

    default:
      startDate = new Date();
      endDate = today;
      dateLabel = ``;
      break;
  }
  // Format dates as strings (you can adjust the format as needed)
  const startDateString = startDate?.toISOString()?.split('T')[0];
  const endDateString = endDate?.toISOString()?.split('T')[0];

  return { startDate: startDateString, endDate: endDateString, dateLabel };
};

export const getPreviousDateRange = (selectedOption, fiscalYearDetails) => {
  let previousDay = new Date();
  let prevStartDate, prevEndDate, prevDateLabel;

  switch (selectedOption) {
    case '1m':
      previousDay.setMonth(previousDay.getMonth() - 1);
      prevStartDate = new Date(
        previousDay.getFullYear(),
        previousDay.getMonth() - 1,
        previousDay.getDate() + 1
      );
      prevEndDate = previousDay;

      //set UTC time
      prevStartDate = new Date(
        Date.UTC(
          prevStartDate.getFullYear(),
          prevStartDate.getMonth(),
          prevStartDate.getDate(),
          0,
          0,
          0
        )
      );
      prevEndDate = new Date(
        Date.UTC(
          prevEndDate.getFullYear(),
          prevEndDate.getMonth(),
          prevEndDate.getDate(),
          0,
          0,
          0
        )
      );

      prevDateLabel = `${getMonthAbbreviation(
        prevStartDate.getMonth()
      )} ${prevStartDate.getDate()} - ${getMonthAbbreviation(
        prevEndDate.getMonth()
      )} ${prevEndDate.getDate()}`;
      break;

    case '3m':
      previousDay.setMonth(previousDay.getMonth() - 3);

      prevStartDate = new Date(
        previousDay.getFullYear(),
        previousDay.getMonth() - 3,
        previousDay.getDate() + 1
      );
      prevEndDate = previousDay;

      //set UTC time
      prevStartDate = new Date(
        Date.UTC(
          prevStartDate.getFullYear(),
          prevStartDate.getMonth(),
          prevStartDate.getDate(),
          0,
          0,
          0
        )
      );
      prevEndDate = new Date(
        Date.UTC(
          prevEndDate.getFullYear(),
          prevEndDate.getMonth(),
          prevEndDate.getDate(),
          0,
          0,
          0
        )
      );

      prevDateLabel = `${getMonthAbbreviation(
        prevStartDate.getMonth()
      )} ${prevStartDate.getFullYear()} - ${getMonthAbbreviation(
        prevEndDate.getMonth()
      )} ${prevEndDate.getFullYear()}`;
      break;

    case '6m':
      previousDay.setMonth(previousDay.getMonth() - 6);

      prevStartDate = new Date(
        previousDay.getFullYear(),
        previousDay.getMonth() - 6,
        previousDay.getDate() + 1
      );
      prevEndDate = previousDay;

      //set UTC time
      prevStartDate = new Date(
        Date.UTC(
          prevStartDate.getFullYear(),
          prevStartDate.getMonth(),
          prevStartDate.getDate(),
          0,
          0,
          0
        )
      );
      prevEndDate = new Date(
        Date.UTC(
          prevEndDate.getFullYear(),
          prevEndDate.getMonth(),
          prevEndDate.getDate(),
          0,
          0,
          0
        )
      );

      prevDateLabel = `${getMonthAbbreviation(
        prevStartDate.getMonth()
      )} ${prevStartDate.getFullYear()} - ${getMonthAbbreviation(
        prevEndDate.getMonth()
      )} ${prevEndDate.getFullYear()}`;
      break;

    case 'ytd':
      if (
        fiscalYearDetails &&
        Object.keys(fiscalYearDetails)?.length > 0 &&
        !checkUndefinedOrNull(fiscalYearDetails?.fiscalYearStartDate)
      ) {
        const fiscalYearStartDate = new Date(
          fiscalYearDetails?.fiscalYearStartDate
        );
        prevStartDate = new Date(
          fiscalYearStartDate?.getFullYear() - 1,
          fiscalYearStartDate.getMonth(),
          fiscalYearStartDate.getDate()
        );
      } else {
        prevStartDate = new Date(previousDay.getFullYear() - 1, 0, 1);
      }

      prevEndDate = new Date(
        previousDay.getFullYear() - 1,
        previousDay.getMonth(),
        previousDay.getDate()
      );

      //set UTC time
      prevStartDate = new Date(
        Date.UTC(
          prevStartDate.getFullYear(),
          prevStartDate.getMonth(),
          prevStartDate.getDate(),
          0,
          0,
          0
        )
      );
      prevEndDate = new Date(
        Date.UTC(
          prevEndDate.getFullYear(),
          prevEndDate.getMonth(),
          prevEndDate.getDate(),
          0,
          0,
          0
        )
      );

      prevDateLabel = `Jan ${prevStartDate.getFullYear()} - ${getMonthAbbreviation(
        prevEndDate.getMonth()
      )} ${prevEndDate.getDate()} ${prevEndDate.getFullYear()}`;
      break;

    case '1y':
      previousDay.setFullYear(previousDay.getFullYear() - 1);
      prevStartDate = new Date(
        previousDay.getFullYear() - 1,
        previousDay.getMonth(),
        previousDay.getDate() + 1
      );
      prevEndDate = previousDay;

      //set UTC time
      prevStartDate = new Date(
        Date.UTC(
          prevStartDate.getFullYear(),
          prevStartDate.getMonth(),
          prevStartDate.getDate(),
          0,
          0,
          0
        )
      );
      prevEndDate = new Date(
        Date.UTC(
          prevEndDate.getFullYear(),
          prevEndDate.getMonth(),
          prevEndDate.getDate(),
          0,
          0,
          0
        )
      );
      prevDateLabel = `${getMonthAbbreviation(
        prevStartDate.getMonth()
      )} ${prevStartDate.getFullYear()} - ${getMonthAbbreviation(
        prevEndDate.getMonth()
      )} ${prevEndDate.getFullYear()}`;
      break;

    default:
      prevStartDate = new Date();
      prevEndDate = previousDay;
      prevDateLabel = ``;
      break;
  }
  // Format dates as strings (you can adjust the format as needed)
  const startDateString = prevStartDate?.toISOString()?.split('T')[0];
  const endDateString = prevEndDate?.toISOString()?.split('T')[0];

  return {
    prevStartDate: startDateString,
    prevEndDate: endDateString,
    prevDateLabel
  };
};

export const getMonthAbbreviation = (month) => {
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];
  return monthNames[month];
};

export const filterGranularityByRange = (range) => {
  let granularities = [];

  if (range == undefined) {
    granularities = [
      { name: 'default', title: 'Default' },
      { name: 'withoutGrouping', title: 'w/o grouping' },
      {
        name: 'day',
        title: 'day'
      },
      {
        name: 'week',
        title: 'Week'
      },
      {
        name: 'month',
        title: 'Month'
      },
      {
        name: 'quarter',
        title: 'Quarter'
      },
      {
        name: 'year',
        title: 'Year'
      }
    ];
  } else if (range == '1m') {
    granularities = [
      { name: 'default', title: 'Default' },
      {
        name: 'withoutGrouping',
        title: 'w/o grouping'
      },
      {
        name: 'day',
        title: 'day'
      },
      {
        name: 'week',
        title: 'Week'
      },
      {
        name: 'month',
        title: 'Month'
      }
    ];
  } else if (range == '3m') {
    granularities = [
      { name: 'default', title: 'Default' },
      { name: 'withoutGrouping', title: 'w/o grouping' },
      {
        name: 'day',
        title: 'day'
      },
      {
        name: 'week',
        title: 'Week'
      },
      {
        name: 'month',
        title: 'Month'
      },
      {
        name: 'quarter',
        title: 'Quarter'
      }
    ];
  } else if (range == '6m') {
    granularities = [
      { name: 'default', title: 'Default' },
      { name: 'withoutGrouping', title: 'w/o grouping' },
      {
        name: 'day',
        title: 'day'
      },
      {
        name: 'week',
        title: 'Week'
      },
      {
        name: 'month',
        title: 'Month'
      },
      {
        name: 'quarter',
        title: 'Quarter'
      }
    ];
  } else if (range == '1y') {
    granularities = [
      { name: 'default', title: 'Default' },
      { name: 'withoutGrouping', title: 'w/o grouping' },
      {
        name: 'day',
        title: 'day'
      },
      {
        name: 'week',
        title: 'Week'
      },
      {
        name: 'month',
        title: 'Month'
      },
      {
        name: 'quarter',
        title: 'Quarter'
      },
      {
        name: 'year',
        title: 'Year'
      }
    ];
  } else if (range == 'ytd') {
    granularities = [
      { name: 'default', title: 'Default' },
      { name: 'withoutGrouping', title: 'W/O grouping' },
      {
        name: 'day',
        title: 'day'
      },
      {
        name: 'week',
        title: 'Week'
      },
      {
        name: 'month',
        title: 'Month'
      },
      {
        name: 'quarter',
        title: 'Quarter'
      }
    ];
  } else {
    granularities = [
      { name: 'default', title: 'Default' },
      { name: 'withoutGrouping', title: 'w/o grouping' },
      {
        name: 'day',
        title: 'day'
      },
      {
        name: 'week',
        title: 'Week'
      },
      {
        name: 'month',
        title: 'Month'
      },
      {
        name: 'quarter',
        title: 'Quarter'
      },
      {
        name: 'year',
        title: 'Year'
      }
    ];
  }
  return granularities;
};

export const getDefaultGranularity = (range) => {
  if (range == '1m') {
    return 'week';
  } else if (range == '3m') {
    return 'month';
  } else if (range == '6m') {
    return 'month';
  } else if (range == '1y') {
    return 'month';
  } else if (range == 'ytd') {
    return 'month';
  } else if ((range = 'custom')) {
    return 'month';
  } else {
    return 'month';
  }
};

export const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const getYearDifferences = (startDate, endDate) => {
  // Calculate the difference in milliseconds
  const timeDifference = endDate.getTime() - startDate.getTime();

  // Convert the difference to years
  const yearsDifference = timeDifference / (1000 * 3600 * 24 * 365.25);

  // Round down to the nearest whole number to get the whole years
  const roundedYearsDifference = Math.floor(yearsDifference);

  return roundedYearsDifference;
};
